import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "../../../../services/request";
import {
  base64ToFile,
  getBusinessBoxPath,
  getUserProfilePath,
  getWheelUploadPath,
  handleFileUpload,
} from "../../../../utils/fileUpload";
import {
  fetchImageAsBase64,
  getLoggedInUser,
  getRandomId,
  isBase64Image,
} from "../../../../utils/helpers";
import Button from "../../../Button";
import Input from "../../../Input";
import TextView from "../../../TextView";
import {
  getDocumentId,
  getEmbedPlaceholderText,
  isDocumentValid,
} from "./helpers";
import { getEmbedCode } from "./embedCode";
import { isAllowedVideoEmbedUrl } from "../../../../utils/urlValidation";
import { useSelector } from "react-redux";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  saveRecentMedia,
  saveExternalImage,
  rehostEmbedImage,
} from "../../../../services/api";
import { EMBED_URL_REHOST_ENABLED } from "../../../../constants/featureFlags";
import { toErrorToastMessage } from "../../../../utils/errorToastMessage";
import { toast } from "react-toastify";

const Container = styled.div`
  display: flex;
  margin-top: 10px;
  flex-direction: column;
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorContainer = styled.div`
  display: flex;
  background-color: #fff4f5;
  padding: 5px;
  border-radius: 8px;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const DisclaimerContainer = styled.div`
  display: flex;
  background-color: #f79a001a;
  padding: 12px;
  border-radius: 8px;
  align-items: flex-start;
  gap: 10px;
  margin-top: 15px;
  border: 1px solid #f79a00;
`;

export default function EmbedURL({
  type = "image",
  onConfirm,
  onError,
  defaultValue,
  pathType = "",
  media,
  showDisclaimer = false,
  onExternalChange = null,
}) {
  const [value, setValue] = useState(media?.originalSrc || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const wheelId = useSelector(getWheelId);
  const user_id = getLoggedInUser().id;

  const getUploadPath = () => {
    if (pathType === "profile") {
      return getUserProfilePath({ user_id });
    } else if (pathType === "bussinessBox") {
      return getBusinessBoxPath({ user_id });
    } else {
      return getWheelUploadPath({ user_id, wheel_id: wheelId });
    }
  };

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  function getDocumentType(url) {
    const urlParts = url.split("/");
    if (urlParts.includes("document")) {
      return "g-docs";
    } else if (urlParts.includes("spreadsheets")) {
      return "g-sheets";
    } else if (urlParts.includes("presentation")) {
      return "g-slides";
    } else if (urlParts.includes("forms")) {
      return "g-forms";
    } else if (url.includes("drive.google.com")) {
      return "g-drive";
    } else if (url.includes("sharepoint.com")) {
      return "onedrive";
    } else {
      return "";
    }
  }


  const getFileType = () => {
    switch (type) {
      case "images":
        return "0";
      case "image":
        return "0";
      case "video":
        return "1";
      case "documents":
        return "2";
      case "audio":
        return "3";
    }
  };

  const onConfirmHandler = async () => {
    setError("");
    let url = value;
    const uploadPath = getUploadPath();

    switch (type) {
      case "images":
      case "image": {
        // EMBED_URL_REHOST: a pasted external image URL is fetched, moderated and
        // re-hosted server-side (SSRF-guarded) instead of being hotlinked /
        // client-fetched. Only applies to real URLs; a base64/data-URI paste
        // keeps the existing client-side upload path. When the flag is OFF this
        // block is skipped entirely and behavior is byte-identical to today.
        if (EMBED_URL_REHOST_ENABLED && !isBase64Image(value)) {
          const rehostId = getRandomId();
          try {
            onConfirm({ id: rehostId, loading: true });
            const resp = await rehostEmbedImage({
              url: value,
              wheel_id: wheelId,
            });
            const uploadedUrl = resp?.data?.data?.url;
            onConfirm({
              id: rehostId,
              src: uploadedUrl,
              title: "",
              originalSrc: value,
              loading: false,
            });
            await saveRecentMedia(wheelId, {
              type: getFileType(),
              url: uploadedUrl,
            });
          } catch (err) {
            if (onError) onError(rehostId);
            else onConfirm({ id: rehostId, loading: false });
            const flagged = err?.response?.data?.data?.flagged;
            const apiMsg = err?.response?.data?.message;
            if (flagged) {
              // Same quarantine/reject UX as an upload: surface the reason
              // inline and do NOT insert the image.
              setError(
                apiMsg ||
                  "This image was flagged by moderation and cannot be used."
              );
            } else {
              toast.error(
                toErrorToastMessage(
                  apiMsg,
                  "Failed to add image from URL. Please try uploading directly."
                )
              );
            }
          }
          return;
        }

        let file;
        const uploadId = getRandomId();
        if (isBase64Image(value)) file = base64ToFile(value);
        else {
          try {
            const base64String = await fetchImageAsBase64(url);
            file = base64ToFile(base64String, "Untitled_image");
          } catch (error) {
            //If the image is not accessible due to CORS, upload it via backend API
            try {
              
              onConfirm({ id:uploadId, loading: true });
              const saveResponse = await saveExternalImage({
                url: value,
                wheel_id: wheelId,
              });
              const uploadedUrl = saveResponse?.data?.data?.url;
              onConfirm({
                id: uploadId,
                src: uploadedUrl,
                title: "",
                originalSrc: value,
                loading: false,
              });
              await saveRecentMedia(wheelId, {
                type: getFileType(),
                url: uploadedUrl,
              });
            } catch (uploadError) {
              console.error("Failed to upload external image:", uploadError);
              if (onError) onError(uploadId);
              else onConfirm({ id: uploadId, loading: false });
              toast.error(
                "Failed to upload image. Please try uploading directly."
              );
            }
            return;
          }
        }
    
        try {
          onConfirm({ id: uploadId, loading: true });
          const { location } = await handleFileUpload(file, uploadPath);
          url = location;
          onConfirm({
            id: uploadId,
            src: url,
            title: "",
            originalSrc: value,
            loading: false,
          });
          await saveRecentMedia(wheelId, {
            type: getFileType(),
            url: url,
            metadata: {
              mimeType: file.type,
            },
          });
        } catch (error) {
          if (onError) onError(uploadId);
          else onConfirm({ id: uploadId, loading: false });
          toast.error(
            toErrorToastMessage(error, "Couldn't finish adding this image. Please try again.")
          );
        }

        break;
      }

      case "video":
        // Security (nemesis #138): the embed URL becomes an <iframe src> on the
        // public viewer page. Only accept a link from an allow-listed video host
        // (YouTube, Vimeo, Instagram, …); reject arbitrary/unsafe hosts and
        // dangerous schemes (javascript:/data:) instead of embedding them raw.
        if (!isAllowedVideoEmbedUrl(value)) {
          setError(
            "Enter a valid video link from a supported host (YouTube, Vimeo, Instagram, Loom, and similar)."
          );
          return;
        }
        url = getEmbedCode(value);
        onConfirm({ src: url, title: "Untitled video" });
        // gordon-0921-DEV-edit-wheel-red-toast-shows-only-the-number-3: this
        // save had NO try/catch at all -- a failure here (measured live: a
        // 500 from save-recent-media) was an uncaught promise rejection with
        // ZERO user-visible feedback, not even a toast. The video embed
        // above already succeeded, so this failure is only about the Recent
        // Media bookkeeping, not the video itself.
        try {
          await saveRecentMedia(wheelId, {
            type: getFileType(),
            url,
            originalSrc: value,
          });
        } catch (error) {
          toast.error(
            toErrorToastMessage(
              error,
              "Video added, but couldn't save it to your Recent Media."
            )
          );
        }
        break;

      case "wheel":
        onConfirm(url);
        break;

      case "document":
      case "documents": {
        // The host allowlist (Google Drive/Docs/Sheets/Slides/Forms, OneDrive)
        // is the real gate. The previous client-side HEAD reachability probe
        // (`fetch(url, { method: "HEAD" })`) was doomed by CORS — a cross-origin
        // HEAD to Google/OneDrive throws, which the check misread as
        // "unreachable" and used to REJECT valid public docs (false negative —
        // C7.4/CM7/EC12). Embed directly once the URL is allowlisted; if the
        // link isn't actually shared, the embedded viewer surfaces its own
        // access page (the correct place for that feedback).
        if (!isDocumentValid(url)) {
          setError(
            "Only the following URL sources are accepted: Google Drive, Google Docs, Google Sheets, and One Drive."
          );
          return;
        }
        const id = getRandomId();
        setLoading(true);
        try {
          onConfirm({
            id,
            loading: false,
            src: url,
            title: getDocumentId(url),
            type: getDocumentType(url),
            originalSrc: value,
          });
          await saveRecentMedia(wheelId, {
            type: getFileType(),
            url,
            metadata: {
              title: getDocumentId(url),
              type: getDocumentType(url),
            },
          });
        } catch (error) {
          // gordon-0921-DEV-edit-wheel-red-toast-shows-only-the-number-3: same
          // gap as the video case above -- this save had NO catch at all, so a
          // failure was an uncaught promise rejection with zero feedback. The
          // document embed above already succeeded; only Recent Media failed.
          toast.error(
            toErrorToastMessage(
              error,
              "Document added, but couldn't save it to your Recent Media."
            )
          );
        } finally {
          setLoading(false);
        }
        break;
      }
    }
  };

  const getPlaceholderText = () => getEmbedPlaceholderText(type);

  return (
    <Container>
      <TextView>{getPlaceholderText()}</TextView>
      <Row>
        <Input
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (onExternalChange) onExternalChange(e.target.value);
          }}
          onBlur={(e) => {
            if (onExternalChange) onExternalChange(e.target.value);
          }}
          variant={"secondary"}
          containerStyle={{ width: "100%", marginBottom: 0, marginRight: 10 }}
          inputStyle={{
            borderColor: error && "#B5251F",
            color: error && "#B5251F",
          }}
        />

        <Button
          isLoading={loading}
          variant="primary"
          disabled={!value}
          style={{ width: 100, height: 36, fontSize: 12 }}
          onClick={onConfirmHandler}
        >
          Confirm
        </Button>
      </Row>
      {showDisclaimer && !EMBED_URL_REHOST_ENABLED && (
        <DisclaimerContainer>
          <TextView
            variant="largeText"
            color={"#565556"}
            style={{ fontSize: 14, lineHeight: 1.4 }}
          >
            <strong>Warning:</strong> Wheel Portraits and Wallpapers added
            through the Embed URL feature may not display correctly on social
            media. As an alternative, please download the image and upload it
            using the Upload Tab.
          </TextView>
        </DisclaimerContainer>
      )}
      {error && (
        <ErrorContainer>
          <ReportProblemIcon style={{ color: "#B5251F" }} />
          <TextView variant="tag" color={"#B5251F"} style={{ fontSize: 12 }}>
            {error}
          </TextView>
        </ErrorContainer>
      )}
    </Container>
  );
}
